import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
  Modal,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Rect, Circle } from 'react-native-svg';
import * as DocumentPicker from 'expo-document-picker';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, SelectDropdown, Breadcrumb } from '../../components';
import { useTargetDepartment } from '../../hooks/useTargetDepartment';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { employeesApi, organizationalUnitsApi, organizationsApi, kpisApi } from '../../services/apiService';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { resolveKpiTarget, validateKpiTargetConfigs } from '../../utils/kpiErrors';
import { OrganizationalUnitList, OrganizationList, KPIType, Frequency, Polarity, KPIList } from '../../types';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { IconButton } from '@design/components/IconButton';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  configFromKpi,
  buildKpiTargetPayload,
} from '../kpis/components/KpiAnnualTargetSection';
// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: number;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  startDate: string;
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  isExisting?: boolean;
  apiId?: number;
}

type RouteProps = RouteProp<MainStackParamList, 'AddEmployee'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UploadIcon = ({ size = 24, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Options
// ============================================================

interface DropdownOption {
  value: string | number;
  label: string;
}

// ============================================================
// Main Component
// ============================================================

export default function AddEmployeeScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { employeeId, unitId, source } = route.params || {};
  const isFromEmployees = source === 'employees';
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);

  useEffect(() => {
    if (isViewOnly) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, navigation, navigateToHome]);

  // Dropdown options with translations
  const genderOptions: DropdownOption[] = [
    { value: 'male', label: t('employees.male') },
    { value: 'female', label: t('employees.female') },
  ];

  const userTypeOptions: DropdownOption[] = [
    { value: 'employee', label: t('employees.userTypes.employee') },
    { value: 'adminEmployee', label: t('employees.userTypes.adminEmployee') },
    { value: 'manager', label: t('employees.userTypes.manager') },
    { value: 'orgManager', label: t('employees.userTypes.orgManager') },
  ];

  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('employees.strategic') },
    { value: 'operational', label: t('employees.operational') },
    { value: 'efficiency', label: t('employees.efficiency') },
    { value: 'effectiveness', label: t('employees.effectiveness') },
    { value: 'impact', label: t('employees.impact') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('employees.ascending') },
    { value: 'negative', label: t('employees.descending') },
  ];

  const kpiUnitOptions: DropdownOption[] = [
    { value: '%', label: t('employees.percentage') },
    { value: 'number', label: t('employees.number') },
    { value: 'currency', label: t('employees.currency') },
    { value: 'day', label: t('employees.day') },
    { value: 'hour', label: t('employees.hour') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'monthly', label: t('employees.monthly') },
    { value: 'quarterly', label: t('employees.quarterly') },
    { value: 'half_yearly', label: t('employees.semiAnnual') },
    { value: 'yearly', label: t('employees.yearly') },
  ];

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    uploadArea: isDark ? colors.backgroundAlt : '#FAFAFA',
    uploadAreaBorder: isDark ? colors.border : tokens.colors.raw.slate["200"],
    loadingText: colors.textMuted,
  };

  // Edit mode flag
  const isEditMode = !!employeeId;
  // Id of an employee created earlier in THIS screen session. The employee is
  // persisted before its KPIs, so if a KPI then fails the record already exists;
  // remembering its id makes a retry UPDATE that same employee instead of
  // POSTing a second one (which collides on the unique email → "البريد موجود").
  const [createdEmployeeId, setCreatedEmployeeId] = useState<number | null>(null);
  const isSmallScreen = width < 768;
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  // Department this employee is saved under. Single-department users always
  // get their own. A multi-department user's employee follows the picked unit
  // (see targetDepartmentId in handleSubmit).
  const { isMultiDepartment, departmentId, setDepartmentId } = useTargetDepartment();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Data lists
  const [organizations, setOrganizations] = useState<OrganizationList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);

  // المعلومات الشخصية
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('male');

  // Profile Image
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<any>(null);
  const [profileImageName, setProfileImageName] = useState<string>('');

  // Date Picker Modal
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(1990);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedDay, setSelectedDay] = useState(1);

  // Profile information
  const [userType, setUserType] = useState('employee');
  const [jobTitle, setJobTitle] = useState('');
  const [organization, setOrganization] = useState<number | null>(departmentId || null);
  const [department, setDepartment] = useState<number | null>(null);
  const [organizationalUnit, setOrganizationalUnit] = useState<number | null>(unitId || null);

  // KPIs state
  useUnsavedChangesWarning(!!firstName || !!lastName || !!email);

  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [nextKpiId, setNextKpiId] = useState(1);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  const [existingKpisLoading, setExistingKpisLoading] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionY = useRef<number>(0);


  // ============================================================
  // Data Fetching
  // ============================================================

  // Ensure org_employee users have their organization set
  useEffect(() => {
    if (isOrgEmployee && departmentId) {
      setOrganization(departmentId);
    }
  }, [isOrgEmployee, departmentId]);

  useEffect(() => {
    fetchInitialData();
  }, [employeeId]);

  // Units the form offers: every unit the user can see, across all the
  // departments they manage. A multi-department user's new employee is saved
  // under the department of the unit they pick (see targetDepartmentId).
  const loadUnits = async (): Promise<OrganizationalUnitList[]> => {
    const unitsData = await organizationalUnitsApi.list({ page_size: 100 });
    const results = unitsData.results || [];
    setUnits(results);
    return results;
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // An edited employee keeps their department unless a unit in another
      // one is picked (see targetDepartmentId).
      const editedEmployee = employeeId ? await employeesApi.get(employeeId) : null;
      if (isMultiDepartment && editedEmployee?.department_id) {
        setDepartmentId(editedEmployee.department_id);
      }

      const [orgsData, unitsResults] = await Promise.all([
        organizationsApi.list({ page_size: 100 }),
        loadUnits(),
      ]);
      const unitsData = { results: unitsResults };

      // Added from a section: show the department it sits under.
      if (!editedEmployee && unitId) {
        const parent = unitsResults.find(u => u.id === unitId)?.parent;
        if (parent != null && unitsResults.some(u => u.id === parent && u.unit_type === 'department')) {
          setDepartment(parent);
        }
      }

      setOrganizations(orgsData.results || []);

      if (employeeId && editedEmployee) {
        const employeeData = editedEmployee;
        const names = (employeeData.name_ar || '').split(' ');
        setFirstName(names[0] || '');
        setLastName(names.slice(1).join(' ') || '');
        setPhone(employeeData.phone || '');
        setEmail(employeeData.email || '');
        setJobTitle(employeeData.position || '');
        if (employeeData.user_type) {
          setUserType(employeeData.user_type);
        }
        // For org_employee users, don't override their organization
        if (!isOrgEmployee) {
          setOrganization(employeeData.organization);
        }
        setOrganizationalUnit(employeeData.organizational_unit);

        // Set the department based on the organizational unit's parent
        if (employeeData.organizational_unit && unitsData.results) {
          const employeeUnit = unitsData.results.find((u: any) => u.id === employeeData.organizational_unit);
          if (employeeUnit) {
            // If the unit is a section, its parent is the department
            if (employeeUnit.unit_type === 'section' && employeeUnit.parent) {
              setDepartment(employeeUnit.parent);
            }
            // If the unit is a department itself, set it as the department
            else if (employeeUnit.unit_type === 'department') {
              setDepartment(employeeUnit.id);
              setOrganizationalUnit(null);
            }
          }
        }

        // Load new fields
        if (employeeData.birth_date) {
          setBirthDate(employeeData.birth_date);
        }
        if (employeeData.gender) {
          setGender(employeeData.gender);
        }
        if (employeeData.profile_image) {
          setProfileImage(employeeData.profile_image);
          setProfileImageName(t('employees.employeeImage'));
        }

        // Fetch KPIs linked to this employee
        setExistingKpisLoading(true);
        try {
          console.log('Fetching KPIs for employee ID (edit mode):', employeeId);
          const kpisData = await kpisApi.list({ employee: employeeId, page_size: 100 });
          console.log('KPIs fetched (edit mode):', kpisData.results?.length || 0, kpisData.results);
          const kpiList = kpisData.results || [];
          // Fetch full details for each existing KPI to get all fields
          const fullKpis = await Promise.all(
            kpiList.map((kpi: KPIList) => kpisApi.get(kpi.id).catch(() => null))
          );
          const existingKpiData: KPIFormData[] = kpiList.map((kpi: KPIList, index: number) => {
            const full = fullKpis[index];
            return {
              id: -(index + 1),
              name: kpi.name,
              weight: String(full?.weight ?? kpi.weight ?? '30'),
              description: full?.description || '',
              kpiType: (full?.kpi_type || kpi.kpi_type || 'operational') as KPIType,
              polarity: (full?.polarity || 'positive') as Polarity,
              startDate: full?.measurement_start_date || '',
              unit: full?.unit || kpi.unit || '',
              frequency: (full?.frequency || kpi.frequency || 'monthly') as Frequency,
              formula: full?.measurement_formula || '',
              targetValue: String(full?.target_value ?? kpi.target_value ?? '0'),
              worstAcceptableValue:
                full?.worst_acceptable_value != null
                  ? String(full.worst_acceptable_value)
                  : ((kpi as any).worst_acceptable_value != null ? String((kpi as any).worst_acceptable_value) : ''),
              dataSource: full?.data_source || full?.owner || '',
              target: configFromKpi(full ?? kpi),
              isExpanded: false,
              isExisting: true,
              apiId: kpi.id,
            };
          });
          setKpis(existingKpiData);
          setNextKpiId(existingKpiData.length + 1);
        } catch (err) {
          console.error('Error fetching KPIs:', err);
        } finally {
          setExistingKpisLoading(false);
        }
      }
    } catch (err: any) {
      showError(err.message || t('employees.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Date Picker Handlers
  // ============================================================

  const monthNames = [
    t('employees.months.january'), t('employees.months.february'), t('employees.months.march'),
    t('employees.months.april'), t('employees.months.may'), t('employees.months.june'),
    t('employees.months.july'), t('employees.months.august'), t('employees.months.september'),
    t('employees.months.october'), t('employees.months.november'), t('employees.months.december')
  ];

  const openDatePicker = () => {
    // Parse existing date if available
    if (birthDate) {
      const parts = birthDate.split('-');
      if (parts.length === 3) {
        setSelectedYear(parseInt(parts[0]) || 1990);
        setSelectedMonth(parseInt(parts[1]) || 1);
        setSelectedDay(parseInt(parts[2]) || 1);
      }
    }
    setShowDatePicker(true);
  };

  const confirmDate = () => {
    const formattedDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    setBirthDate(formattedDate);
    setShowDatePicker(false);
  };

  const getDisplayDate = () => {
    if (!birthDate) return '';
    const parts = birthDate.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1]) - 1;
      const day = parts[2];
      return `${day} ${monthNames[month]}, ${year}`;
    }
    return birthDate;
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  // ============================================================
  // Image Picker Handlers
  // ============================================================

  const pickImage = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        setProfileImage(asset.uri);
        setProfileImageFile(asset);
        setProfileImageName(asset.name || t('employees.imagePng'));
      }
    } catch (err) {
      console.error('Error picking image:', err);
      showError(t('employees.imageUploadFailed'));
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setProfileImageFile(null);
    setProfileImageName('');
  };

  // ============================================================
  // KPI Handlers
  // ============================================================

  const handleAddKPI = () => {
    const newKpi: KPIFormData = {
      id: nextKpiId,
      name: '',
      weight: '30',
      description: '',
      kpiType: 'operational',
      polarity: '',
      startDate: '',
      unit: '%',
      frequency: 'yearly',
      formula: '',
      targetValue: '0',
      worstAcceptableValue: '',
      dataSource: '',
      target: emptyKpiTargetConfig,
      isExpanded: true,
      isExisting: false, // new KPI: validator must read the grid, not the scalar targetValue
    };
    setKpis([...kpis, newKpi]);
    setNextKpiId(nextKpiId + 1);
  };

  const updateKPI = (id: number, data: Partial<KPIFormData>) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, ...data } : kpi));
  };

  const deleteKPI = (id: number) => {
    setKpis(kpis.filter(kpi => kpi.id !== id));
  };

  const toggleKPIExpand = (id: number) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi));
  };

  // ============================================================
  // Form Submission
  // ============================================================

  // Map backend field names to user-friendly Arabic labels
  const fieldLabels: Record<string, string> = {
    name_ar: t('employees.name'),
    name_en: t('employees.name'),
    email: t('employees.email'),
    phone: t('employees.phone'),
    position: t('employees.jobTitle'),
    department_id: t('employees.organization'),
    organizational_unit: t('employees.organizationalUnit'),
    birth_date: t('employees.birthDate'),
    gender: t('employees.gender'),
    employee_number: t('employees.employeeNumber') || 'الرقم الوظيفي',
    non_field_errors: '',
  };

  const parseApiErrors = (error: any): string => {
    const data = error.response?.data;
    if (!data) return error.message || t('employees.saveFailed');

    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;

    // Parse field-level validation errors
    const messages: string[] = [];
    for (const [field, errors] of Object.entries(data)) {
      const label = fieldLabels[field] || field;
      const errorText = Array.isArray(errors) ? errors.join('، ') : String(errors);
      if (field === 'non_field_errors') {
        messages.push(errorText);
      } else {
        messages.push(`${label}: ${errorText}`);
      }
    }
    return messages.join('\n') || t('employees.saveFailed');
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) {
      showError(t('employees.firstNameRequired'));
      return;
    }

    // Validate email format if provided
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        showError(t('employees.invalidEmail'));
        return;
      }
    }

    // Validate KPI required fields
    const kpisWithName = kpis.filter(kpi => kpi.name && kpi.name.trim());
    for (const kpi of kpisWithName) {
      const missing: string[] = [];
      if (!kpi.name.trim()) missing.push(t('employees.kpiNameLabel'));
      if (!kpi.weight) missing.push(t('employees.weightLabel'));
      if (!kpi.polarity) missing.push(t('employees.polarityLabel'));
      if (!kpi.kpiType) missing.push(t('kpis.kpiClassification'));
      if (!kpi.unit) missing.push(t('employees.measurementUnitLabel'));
      if (!kpi.frequency) missing.push(t('employees.measurementFrequencyLabel'));
      // Read the EFFECTIVE target (annual grid total when authorable, else the
      // scalar). The target is authored through the grid, so the raw scalar
      // `targetValue` stays at its '0' sentinel and reading it directly flagged
      // a false "القيمة المستهدفة" even when the grid was fully filled.
      const targetNum = resolveKpiTarget(kpi, 'startDate');
      const isNegative = kpi.polarity === 'negative';

      // Non-negative KPIs need a target > 0; negative-polarity KPIs may target 0
      // or a negative value. worst_acceptable_value validation removed — the field
      // is deprecated in the UI (kept in sync with kpiErrors.ts + the backend).
      if (isNaN(targetNum) || (!isNegative && targetNum <= 0)) {
        missing.push(t('employees.targetValueLabel'));
      }

      if (!kpi.startDate) missing.push(t('kpis.measurementStartDate'));
      if (missing.length > 0) {
        showError(`${t('employees.kpiNumber', { number: kpis.indexOf(kpi) + 1 })}: ${missing.join('، ')}`);
        return;
      }
    }

    // Validate KPI annual-target config (verification/measurement-end date,
    // pattern, step values) — mirrors KPISerializer.validate() so a missing end
    // date is caught here instead of round-tripping to a swallowed 400.
    const kpiTargetError = validateKpiTargetConfigs(kpisWithName, t);
    if (kpiTargetError) {
      showError(kpiTargetError);
      return;
    }

    setSubmitting(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

      // A multi-department user's employee goes under the picked unit's
      // department; with no unit, an edited employee keeps theirs and a new
      // one goes under the user's own (root) department.
      const selectedUnitId = organizationalUnit ?? department;
      const selectedUnitDepartmentId =
        units.find(u => u.id === selectedUnitId)?.department_id ?? null;
      const targetDepartmentId = isMultiDepartment
        ? (selectedUnitDepartmentId ?? departmentId ?? user?.profile?.organization ?? null)
        : departmentId;

      // For org_employee users, always use their target department
      const employeeOrganization = isOrgEmployee && targetDepartmentId
        ? targetDepartmentId
        : (organization ?? targetDepartmentId ?? undefined);

      const employeeData: Record<string, any> = {
        name_ar: fullName,
        name_en: fullName,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        position: jobTitle.trim() || undefined,
        department_id: employeeOrganization,
        organizational_unit: organizationalUnit ?? department ?? undefined,
        birth_date: birthDate || undefined,
        gender: gender || 'male',
        user_type: userType,
      };

      // An existing record = the one we're editing (route param) OR one this
      // session already created (a prior save that persisted the employee but
      // failed on a later step). Either way we UPDATE it rather than create a
      // duplicate.
      const existingEmployeeId = employeeId ?? createdEmployeeId;

      // Only set employee_number when creating a brand-new employee
      if (!existingEmployeeId) {
        employeeData.employee_number = `EMP-${Date.now()}`;
      }

      let savedEmployeeId: number;

      if (existingEmployeeId) {
        await employeesApi.update(existingEmployeeId, employeeData);
        savedEmployeeId = existingEmployeeId;
      } else {
        const response = await employeesApi.create(employeeData);
        savedEmployeeId = response.id;
        // Remember it immediately so that if a later step (image/KPI) throws,
        // the next save updates this employee instead of creating a duplicate.
        setCreatedEmployeeId(response.id);
      }

      // Handle profile image upload if a new image was selected
      if (profileImageFile && profileImageFile.uri) {
        try {
          const formData = new FormData();

          if (Platform.OS === 'web') {
            // On web, we need to fetch the blob from the URI
            const response = await fetch(profileImageFile.uri);
            const blob = await response.blob();
            const file = new File([blob], profileImageFile.name || 'profile.jpg', {
              type: profileImageFile.mimeType || 'image/jpeg',
            });
            formData.append('profile_image', file);
          } else {
            // On native, use the React Native format
            formData.append('profile_image', {
              uri: profileImageFile.uri,
              name: profileImageFile.name || 'profile.jpg',
              type: profileImageFile.mimeType || 'image/jpeg',
            } as any);
          }

          await employeesApi.uploadImage(savedEmployeeId, formData);
        } catch (imgErr) {
          console.error('Error uploading profile image:', imgErr);
          // Continue even if image upload fails
        }
      }

      // Handle KPIs - update existing ones and create new ones
      // Only process KPIs that have a name (required field)
      const validKpis = kpis.filter(kpi => kpi.name && kpi.name.trim());

      if (validKpis.length > 0) {
        for (const kpi of validKpis) {
          const kpiData: Record<string, any> = {
            name: kpi.name,
            description: kpi.description || '',
            kpi_type: kpi.kpiType || 'operational',
            classification: kpi.kpiType || 'operational',
            unit: kpi.unit || '%',
            target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
            frequency: kpi.frequency || 'monthly',
            polarity: kpi.polarity || 'positive',
            weight: parseFloat(kpi.weight) || 30,
            weight_from_source: parseFloat(kpi.weight) || 30,
            source_type: 'employee',
            evidence_required: false,
            owner: kpi.dataSource || '',
            data_source: kpi.dataSource || '',
            measurement_formula: kpi.formula || '',
            measurement_start_date: kpi.startDate || new Date().toISOString().split('T')[0],
            employee: savedEmployeeId,
          };

          try {
            if (kpi.isExisting && kpi.apiId) {
              await kpisApi.update(kpi.apiId, {
                ...kpiData,
                ...buildKpiTargetPayload(kpi.target, {
                  unit: kpi.unit,
                  frequency: kpi.frequency,
                  startDate: kpi.startDate,
                }),
              });
            } else {
              const createdKpi = await kpisApi.create({
                ...kpiData,
                ...buildKpiTargetPayload(kpi.target, {
                  unit: kpi.unit,
                  frequency: kpi.frequency,
                  startDate: kpi.startDate,
                }),
              });
              // Mark this KPI as saved so that if a LATER kpi in the loop throws
              // and the user retries, this one is UPDATED rather than created a
              // second time on the employee.
              setKpis(prev => prev.map(k => (k.id === kpi.id ? { ...k, isExisting: true, apiId: createdKpi.id } : k)));
            }
          } catch (kpiErr: any) {
            console.error('KPI API Error:', kpiErr.response?.data);
            const errorData = kpiErr.response?.data;
            let errorMessage = kpiErr.message;
            if (errorData) {
              if (typeof errorData === 'string') {
                errorMessage = errorData;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              } else {
                // Convert field errors to readable message
                errorMessage = Object.entries(errorData)
                  .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                  .join('; ');
              }
            }
            throw new Error(t('employees.kpiCreateFailed', { name: kpi.name, error: errorMessage }));
          }
        }
      }

      showSuccess(isEditMode ? t('employees.updateDataSuccess') : t('employees.saveSuccess'));
      invalidateData('my-organization');
      // Go to the saved employee's detail page instead of goBack() (which can
      // land on the dashboard when the stack is shallow).
      if (savedEmployeeId) {
        navigation.navigate('EmployeeDetail', { id: savedEmployeeId });
      } else {
        navigation.goBack();
      }
    } catch (err: any) {
      showError(parseApiErrors(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: {
      firstName,
      lastName,
      email,
      phone,
      birthDate,
      gender,
      profileImage,
      profileImageName,
      userType,
      jobTitle,
      organization,
      department,
      organizationalUnit,
      kpis,
    },
    ready: !loading,
    onExit: handleCancel,
  });

  // ============================================================
  // Render
  // ============================================================

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>{t('employees.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  const organizationOptions: DropdownOption[] = organizations.map(org => ({
    value: org.id.toString(),
    label: org.name,
  }));

  // Filter departments (unit_type === 'department')
  const departmentOptions: DropdownOption[] = units
    .filter(unit => unit.unit_type === 'department')
    .map(unit => ({
      value: unit.id.toString(),
      label: unit.name_ar || unit.name,
    }));

  // Units under the selected department; with none selected, every unit that
  // is not itself a department. The current value always stays listed.
  const unitOptions: DropdownOption[] = units
    .filter(unit =>
      unit.id === organizationalUnit ||
      (department != null ? unit.parent === department : unit.unit_type !== 'department'))
    .map(unit => ({
      value: unit.id.toString(),
      label: unit.name_ar || unit.name,
    }));

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('employees.home') },
            { label: isFromEmployees ? t('employees.humanCapital') : t('employees.myOrg'), route: isFromEmployees ? 'Employees' : 'MyOrganization' },
            { label: isEditMode ? t('employees.editData') : t('employees.addEmployee') },
          ]}
        />

        {/* Page Title */}
        <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('employees.editData') : t('employees.addEmployee')}</Text>

        {/* Personal Information Section */}
        <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
          <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('employees.personalInfoSection')}</Text>

          {/* Row 1: First Name + Last Name */}
          <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
            <View style={[styles.formGroup, { zIndex: 1 }]}>
              <Text style={styles.label}>{t('employees.lastNameLabel')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={styles.input}
                value={lastName}
                onChangeText={setLastName}
                placeholder={t('employees.enterLastName')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                textAlign="right"
              />
            </View>
            <View style={[styles.formGroup, { zIndex: 1 }]}>
              <Text style={styles.label}>{t('employees.firstNameLabel')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={styles.input}
                value={firstName}
                onChangeText={setFirstName}
                placeholder={t('employees.enterFirstName')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                textAlign="right"
              />
            </View>
          </View>

          {/* Row 2: Email + Phone */}
          <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>{t('employees.phoneLabel')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={styles.input}
                value={phone}
                onChangeText={setPhone}
                placeholder={t('employees.enterPhone')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                keyboardType="phone-pad"
                textAlign="right"
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>{t('employees.emailLabel')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                placeholder={t('employees.enterEmail')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                keyboardType="email-address"
                autoCapitalize="none"
                textAlign="right"
              />
            </View>
          </View>

          {/* Row 3: Birth Date + Gender */}
          <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
            <View style={[styles.formGroup, { zIndex: 10 }]}>
              <SelectDropdown
                label={t('employees.genderLabel')}
                required
                value={gender}
                options={genderOptions}
                placeholder={t('employees.genderPlaceholder')}
                onChange={(v) => setGender(String(v))}
                zIndex={1010}
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>{t('employees.birthDateLabel')} <Text style={styles.required}>*</Text></Text>
              <TouchableOpacity style={styles.dateInputContainer} onPress={openDatePicker}>
                <CalendarIcon size={18} color={tokens.colors.raw.slate["500"]} />
                <Text style={[styles.dateInputText, !birthDate && styles.dateInputPlaceholder]}>
                  {getDisplayDate() || `16 ${t('employees.months.june')} 1989`}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Profile Image */}
          <View style={styles.formGroup}>
            <Text style={styles.label}>{t('employees.profileImage')}</Text>
            <View style={styles.uploadContainer}>
              {profileImage ? (
                <View style={styles.imagePreviewRow}>
                  <Image source={{ uri: profileImage }} style={styles.imagePreview} />
                  <View style={styles.imagePreviewInfo}>
                    <Text style={styles.imagePreviewName}>{profileImageName}</Text>
                    <View style={styles.imagePreviewActions}>
                      <TouchableOpacity style={styles.changeImageBtn} onPress={pickImage}>
                        <Text style={styles.changeImageBtnText}>{t('employees.changeImage')}</Text>
                      </TouchableOpacity>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<TrashIcon color={tokens.colors.raw.red["500"]} />}
                        accessibilityLabel={t('common.delete')}
                        onPress={removeImage}
                      />
                    </View>
                  </View>
                </View>
              ) : (
                <View style={styles.uploadRow}>
                  <Text style={styles.uploadHint}>{t('employees.uploadHint')}</Text>
                  <View style={styles.uploadActions}>
                    <TouchableOpacity style={styles.uploadBtn} onPress={pickImage}>
                      <UploadIcon size={16} color={tokens.colors.raw.blue["500"]} />
                      <Text style={styles.uploadBtnText}>{t('employees.chooseFile')}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Profile Section */}
        <View style={[styles.section, { zIndex: 5 }]}>
          <Text style={styles.sectionTitle}>{t('employees.profileSection')}</Text>

          {/* Row 1: User Type + Job Title */}
          <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
            <View style={[styles.formGroup, { zIndex: 9 }]}>
              <Text style={styles.label}>{t('employees.jobTitleLabel')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={styles.input}
                value={jobTitle}
                onChangeText={setJobTitle}
                placeholder={t('employees.enterJobTitle')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                textAlign="right"
              />
            </View>
            <View style={[styles.formGroup, { zIndex: 9 }]}>
              <SelectDropdown
                label={t('employees.userTypeLabel')}
                required
                value={userType}
                options={userTypeOptions}
                placeholder={t('employees.userTypes.employee')}
                onChange={(v) => {
                  const newType = String(v);
                  setUserType(newType);
                  if (newType === 'orgManager') {
                    setDepartment(null);
                    setOrganizationalUnit(null);
                  }
                }}
                zIndex={1009}
              />
            </View>
          </View>

          {/* Row 3: Organization - Only show for non-org_employee users */}
          {!isOrgEmployee && (
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
              <View style={[styles.formGroup, { zIndex: 9 }]} />
              <View style={[styles.formGroup, { zIndex: 9 }]}>
                <SelectDropdown
                  label={t('employees.organizationLabel')}
                  required
                  value={organization?.toString() || ''}
                  options={organizationOptions}
                  placeholder={t('employees.organizationsPlaceholder')}
                  onChange={(val) => setOrganization(parseInt(String(val)))}
                  zIndex={1009}
                />
              </View>
            </View>
          )}

          {/* Row 4: Department + Organizational Unit */}
          <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
            <View style={[styles.formGroup, { zIndex: 8 }]}>
              <SelectDropdown
                label={t('employees.orgUnitLabel')}
                value={organizationalUnit?.toString() || ''}
                options={unitOptions}
                placeholder={t('employees.selectOrgUnit')}
                onChange={(val) => {
                  const id = parseInt(String(val));
                  setOrganizationalUnit(id);
                  // Picking a unit first fills in the department it sits under.
                  const parent = units.find(u => u.id === id)?.parent;
                  if (parent != null && units.some(u => u.id === parent && u.unit_type === 'department')) {
                    setDepartment(parent);
                  }
                }}
                zIndex={1008}
                disabled={!!unitId || userType === 'orgManager'}
              />
            </View>
            <View style={[styles.formGroup, { zIndex: 7 }]}>
              <SelectDropdown
                label={t('employees.departmentLabel')}
                required={userType !== 'orgManager'}
                value={department?.toString() || ''}
                options={departmentOptions}
                placeholder={t('employees.selectDepartmentLabel')}
                onChange={(val) => {
                  const id = parseInt(String(val));
                  setDepartment(id);
                  // Drop a picked unit that isn't under the new department
                  // (one fixed by the route stays).
                  if (!unitId) {
                    setOrganizationalUnit(prev =>
                      prev != null && units.find(u => u.id === prev)?.parent !== id ? null : prev);
                  }
                }}
                zIndex={1007}
                disabled={userType === 'orgManager'}
              />
            </View>
          </View>
        </View>

        {/* KPIs Section */}
        <View
          style={[styles.kpisSection, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}
          onLayout={(event) => {
            kpiSectionY.current = event.nativeEvent.layout.y;
          }}
        >
          <View style={[styles.kpisSectionHeader, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"], borderBottomColor: themeColors.sectionBorder }]}>
            <TouchableOpacity
              style={styles.kpiToggleButtonHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.addKpiHeaderButton, { backgroundColor: themeColors.mainCard, borderColor: themeColors.sectionBorder }]}
              onPress={handleAddKPI}
            >
              <Text style={[styles.addKpiHeaderButtonText, { color: themeColors.sectionTitle }]}>{t('employees.addKpi')}</Text>
              <PlusIcon size={14} color={themeColors.sectionTitle} />
            </TouchableOpacity>
            <Text style={[styles.kpisSectionTitle, { color: themeColors.sectionTitle }]}>{t('employees.kpisSection')}</Text>
          </View>

          {kpiSectionExpanded && (
            <View style={styles.kpiCardsContainer}>
              {existingKpisLoading ? (
                <View style={styles.existingKpisLoading}>
                  <ActivityIndicator size="small" color={COLORS.primary} />
                  <Text style={styles.existingKpisLoadingText}>{t('employees.loadingKpis')}</Text>
                </View>
              ) : (
                <>
                  {kpis.map((kpi, index) => (
                    <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.sectionBorder }]}>
                      {/* KPI Header */}
                      <View style={[styles.kpiCardHeader, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"], borderBottomColor: themeColors.sectionBorder }]}>
                        <TouchableOpacity
                          style={styles.kpiToggleButton}
                          onPress={() => toggleKPIExpand(kpi.id)}
                        >
                          {kpi.isExpanded ? (
                            <ChevronUpIcon size={16} color={themeColors.iconColor} />
                          ) : (
                            <ChevronDownIcon size={16} color={themeColors.iconColor} />
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.kpiDeleteButton}
                          onPress={() => deleteKPI(kpi.id)}
                        >
                          <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
                        </TouchableOpacity>
                        <View style={styles.kpiCardHeaderContent}>
                          <Text style={[styles.kpiCardTitle, { color: themeColors.sectionTitle }]}>
                            {kpi.name || t('employees.kpiNumber', { number: index + 1 })}
                          </Text>
                          <View style={[styles.kpiCardNumber, { backgroundColor: COLORS.primary }]}>
                            <Text style={styles.kpiCardNumberText}>{index + 1}</Text>
                          </View>
                        </View>
                        <MenuIcon size={16} color={themeColors.iconColor} />
                      </View>

                      {/* KPI Content - Collapsible */}
                      {kpi.isExpanded && (
                        <View style={styles.kpiCardContent}>
                          {/* Basic Information */}
                          <View style={[styles.kpiFormSection, { zIndex: 40 }]}>
                            <Text style={styles.kpiFormSectionTitle}>{t('employees.basicInfoSection')}</Text>

                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                              <View style={styles.formGroup}>
                                <Text style={styles.label}>{t('employees.weightLabel')} <Text style={styles.required}>*</Text></Text>
                                <View style={styles.weightInputRow}>
                                  <Text style={styles.percentSymbol}>%</Text>
                                  <TextInput
                                    style={[styles.input, { flex: 1 }]}
                                    value={kpi.weight}
                                    onChangeText={(v) => updateKPI(kpi.id, { weight: v })}
                                    keyboardType="numeric"
                                    textAlign="right"
                                  />
                                </View>
                              </View>

                              <View style={styles.formGroup}>
                                <Text style={styles.label}>{t('employees.kpiNameLabel')} <Text style={styles.required}>*</Text></Text>
                                <TextInput
                                  style={styles.input}
                                  value={kpi.name}
                                  onChangeText={(v) => updateKPI(kpi.id, { name: v })}
                                  placeholder={t('employees.enterKpiName')}
                                  placeholderTextColor={tokens.colors.raw.gray["400"]}
                                  textAlign="right"
                                />
                              </View>
                            </View>

                            <View style={styles.formGroup}>
                              <Text style={styles.label}>{t('employees.kpiDescription')}</Text>
                              <TextInput
                                style={[styles.input, styles.textArea]}
                                value={kpi.description}
                                onChangeText={(v) => updateKPI(kpi.id, { description: v })}
                                placeholder={t('employees.enterKpiDescription')}
                                placeholderTextColor={tokens.colors.raw.gray["400"]}
                                textAlign="right"
                                multiline
                                numberOfLines={2}
                              />
                            </View>
                          </View>

                          {/* Classification */}
                          <View style={[styles.kpiFormSection, { zIndex: 30 }]}>
                            <Text style={styles.kpiFormSectionTitle}>{t('employees.classificationSection')}</Text>

                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                              <View style={[styles.formGroup, { zIndex: 32 }]}>
                                <SelectDropdown
                                  label={t('employees.polarityLabel')}
                                  required
                                  value={kpi.polarity}
                                  options={polarityOptions}
                                  placeholder={t('employees.selectPolarity')}
                                  onChange={(v) => updateKPI(kpi.id, { polarity: String(v) as Polarity })}
                                  zIndex={1032}
                                />
                              </View>

                              <View style={[styles.formGroup, { zIndex: 31 }]}>
                                <SelectDropdown
                                  label={t('kpis.kpiClassification')}
                                  required
                                  value={kpi.kpiType}
                                  options={kpiTypeOptions}
                                  placeholder={t('kpis.selectClassification')}
                                  onChange={(v) => updateKPI(kpi.id, { kpiType: String(v) as KPIType })}
                                  zIndex={1031}
                                />
                              </View>
                            </View>
                          </View>

                          <KpiAnnualTargetSection
                            config={kpi.target}
                            onChange={(patch) => updateKPI(kpi.id, { target: { ...kpi.target, ...patch } })}
                            unit={kpi.unit}
                            frequency={kpi.frequency}
                            startDate={kpi.startDate}
                            formula={kpi.formula}
                            dataSource={kpi.dataSource}
                            onMeasurementChange={(field, value) => updateKPI(kpi.id, { [field]: value })}
                          />
                        </View>
                      )}
                    </View>
                  ))}

                  {/* Add KPI Button */}
                  <TouchableOpacity style={[styles.addKpiButton, { backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"], borderColor: themeColors.sectionBorder }]} onPress={handleAddKPI}>
                    <Text style={[styles.addKpiButtonText, { color: themeColors.sectionTitle }]}>{t('employees.addKpiButton')}</Text>
                    <PlusIcon size={16} color={themeColors.sectionTitle} />
                  </TouchableOpacity>
                </>
              )}
            </View>
          )}
        </View>

        {/* Footer Buttons */}
        <View style={styles.footer}>
          <Button
            variant="primary"
            onPress={handleSubmit}
            loading={submitting}
          >
            {t('employees.saveButton')}
          </Button>
          <Button variant="secondary" onPress={requestCancel}>
            {t('employees.cancelButton')}
          </Button>
        </View>
      </ScrollView>

      {/* Date Picker Modal */}
      <Modal
        visible={showDatePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDatePicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.datePickerModal}>
            <Text style={styles.datePickerTitle}>{t('employees.datePickerTitle')}</Text>

            <View style={styles.datePickerContent}>
              {/* Year Selector */}
              <View style={styles.datePickerColumn}>
                <Text style={styles.datePickerLabel}>{t('employees.yearLabel')}</Text>
                <ScrollView style={styles.datePickerScroll} showsVerticalScrollIndicator={false}>
                  {Array.from({ length: 80 }, (_, i) => 2010 - i).map((year) => (
                    <TouchableOpacity
                      key={year}
                      style={[
                        styles.datePickerItem,
                        selectedYear === year && styles.datePickerItemSelected,
                      ]}
                      onPress={() => setSelectedYear(year)}
                    >
                      <Text
                        style={[
                          styles.datePickerItemText,
                          selectedYear === year && styles.datePickerItemTextSelected,
                        ]}
                      >
                        {year}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Month Selector */}
              <View style={styles.datePickerColumn}>
                <Text style={styles.datePickerLabel}>{t('employees.monthLabel')}</Text>
                <ScrollView style={styles.datePickerScroll} showsVerticalScrollIndicator={false}>
                  {monthNames.map((month, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.datePickerItem,
                        selectedMonth === index + 1 && styles.datePickerItemSelected,
                      ]}
                      onPress={() => setSelectedMonth(index + 1)}
                    >
                      <Text
                        style={[
                          styles.datePickerItemText,
                          selectedMonth === index + 1 && styles.datePickerItemTextSelected,
                        ]}
                      >
                        {month}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Day Selector */}
              <View style={styles.datePickerColumn}>
                <Text style={styles.datePickerLabel}>{t('employees.dayLabel')}</Text>
                <ScrollView style={styles.datePickerScroll} showsVerticalScrollIndicator={false}>
                  {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => i + 1).map((day) => (
                    <TouchableOpacity
                      key={day}
                      style={[
                        styles.datePickerItem,
                        selectedDay === day && styles.datePickerItemSelected,
                      ]}
                      onPress={() => setSelectedDay(day)}
                    >
                      <Text
                        style={[
                          styles.datePickerItemText,
                          selectedDay === day && styles.datePickerItemTextSelected,
                        ]}
                      >
                        {day}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>

            <View style={styles.datePickerButtons}>
              <TouchableOpacity
                style={styles.datePickerCancelBtn}
                onPress={() => setShowDatePicker(false)}
              >
                <Text style={styles.datePickerCancelBtnText}>{t('employees.cancelButton')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.datePickerConfirmBtn}
                onPress={confirmDate}
              >
                <Text style={styles.datePickerConfirmBtnText}>{t('employees.confirmButton')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.regular,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  // Page Title
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    color: COLORS.primary,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
    fontFamily: FONTS.medium,
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
    paddingBottom: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    fontFamily: FONTS.medium,
  },
  // Form
  formRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  formRowSmall: {
    flexDirection: 'column',
  },
  formGroup: {
    flex: 1,
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
    fontFamily: FONTS.regular,
  },
  required: {
    color: tokens.colors.raw.red["500"],
  },
  input: {
    height: 44,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    backgroundColor: tokens.colors.raw.white,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    gap: tokens.spacing.absolute["2"],
  },
  dateInput: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  dateInputText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  dateInputPlaceholder: {
    color: tokens.colors.raw.gray["400"],
  },
  // Upload
  uploadContainer: {
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    backgroundColor: '#FAFAFA',
  },
  uploadRow: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["2"],
  },
  uploadActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
  },
  uploadLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  uploadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.white,
  },
  uploadBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.blue["500"],
    fontFamily: FONTS.regular,
  },
  uploadHint: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  // Image Preview
  imagePreviewRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  imagePreview: {
    width: 64,
    height: 64,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.slate["200"],
  },
  imagePreviewInfo: {
    flex: 1,
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  imagePreviewName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  imagePreviewActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  changeImageBtn: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.white,
  },
  changeImageBtnText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.raw.blue["500"],
    fontFamily: FONTS.regular,
  },
  // Footer
  footer: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
  },
  // KPIs Section
  kpisSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.slate["50"],
  },
  kpiToggleButtonHeader: {
    padding: tokens.spacing.absolute["2"],
  },
  addKpiHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
    marginStart: tokens.spacing.absolute["2"],
  },
  addKpiHeaderButtonText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  kpisSectionTitle: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  kpiCardsContainer: {
    padding: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["3"],
  },
  existingKpisLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.absolute["5"],
    gap: tokens.spacing.absolute["2_5"],
  },
  existingKpisLoadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.regular,
  },
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'hidden',
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.slate["50"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  kpiToggleButton: {
    padding: tokens.spacing.absolute["1"],
  },
  kpiDeleteButton: {
    padding: tokens.spacing.absolute["1"],
    marginStart: tokens.spacing.absolute["2"],
  },
  kpiCardHeaderContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginEnd: tokens.spacing.absolute["2"],
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  kpiCardNumber: {
    width: 24,
    height: 24,
    borderRadius: tokens.radii.absolute["radius-12"],
    backgroundColor: COLORS.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardNumberText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  kpiCardContent: {
    padding: tokens.spacing.absolute["4"],
  },
  kpiFormSection: {
    marginBottom: tokens.spacing.absolute["5"],
    paddingBottom: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
  },
  kpiFormSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    fontFamily: FONTS.medium,
  },
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  percentSymbol: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.regular,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
    paddingTop: tokens.spacing.absolute["3"],
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderStyle: 'dashed',
    backgroundColor: tokens.colors.raw.slate["50"],
    marginTop: tokens.spacing.absolute["2"],
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Date Picker Modal
  datePickerModal: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-16"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
  },
  datePickerTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["5"],
    fontFamily: FONTS.medium,
  },
  datePickerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["3"],
  },
  datePickerColumn: {
    flex: 1,
    alignItems: 'center',
  },
  datePickerLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["500"],
    marginBottom: tokens.spacing.absolute["3"],
    textAlign: 'center',
    fontFamily: FONTS.medium,
  },
  datePickerScroll: {
    maxHeight: 200,
    width: '100%',
  },
  datePickerItem: {
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginBottom: tokens.spacing.absolute["1"],
    alignItems: 'center',
  },
  datePickerItemSelected: {
    backgroundColor: COLORS.primary,
  },
  datePickerItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  datePickerItemTextSelected: {
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  datePickerButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["5"],
  },
  datePickerCancelBtn: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.gray["100"],
    alignItems: 'center',
  },
  datePickerCancelBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    fontFamily: FONTS.medium,
  },
  datePickerConfirmBtn: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: COLORS.primary,
    alignItems: 'center',
  },
  datePickerConfirmBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
});

const styles = getStyles(true);
